import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]],
};

const card = { border: "1px solid #ddd", borderRadius: 16, padding: 16, marginTop: 16 };
const input = { padding: 8, borderRadius: 10, border: "1px solid #ddd", width: "100%", boxSizing: "border-box" };
const label = { display: "block", fontSize: 13, opacity: 0.7, marginBottom: 4, marginTop: 10 };
const btn = { padding: "8px 14px", borderRadius: 10, border: "1px solid #ddd", background: "#fff", cursor: "pointer" };
const btnPrimary = { ...btn, background: "#1e88e5", color: "#fff", border: "none" };
const btnDanger = { ...btn, color: "#c0392b" };

export default function AdminContent() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [content, setContent] = useState(null);
  const [savedMsg, setSavedMsg] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [pwError, setPwError] = useState("");

  async function login(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });
      if (!r.ok) throw new Error("Şifre hatalı veya yetkisiz.");
      setAuthed(true);
      setPassword("");
    } catch (err) {
      setError(err.message || "Giriş başarısız.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchContent() {
    setError("");
    setLoading(true);
    try {
      const r = await fetch("/api/admin/content", { credentials: "include" });
      if (r.status === 401) {
        setAuthed(false);
        throw new Error("Oturum kapalı. Tekrar giriş yap.");
      }
      if (!r.ok) throw new Error("İçerik alınamadı.");
      setContent(await r.json());
    } catch (err) {
      setError(err.message || "Hata oluştu.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (authed) fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  function flash(msg) {
    setSavedMsg(msg);
    setTimeout(() => setSavedMsg(""), 2000);
  }

  async function api(path, options = {}) {
    const r = await fetch(path, {
      credentials: "include",
      headers: options.body instanceof FormData ? undefined : { "Content-Type": "application/json" },
      ...options,
    });
    if (r.status === 401) {
      setAuthed(false);
      throw new Error("Oturum kapalı.");
    }
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.error || "İşlem başarısız.");
    }
    return r.json();
  }

  async function changePassword(e) {
    e.preventDefault();
    setPwError("");
    setPwMsg("");
    if (newPassword !== newPassword2) {
      setPwError("Yeni şifreler eşleşmiyor.");
      return;
    }
    try {
      await api("/api/admin/password", {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setPwMsg("Şifre değiştirildi.");
      setCurrentPassword("");
      setNewPassword("");
      setNewPassword2("");
    } catch (err) {
      setPwError(err.message);
    }
  }

  /* ---------- Settings ---------- */

  function updateSetting(field, value) {
    setContent((c) => ({ ...c, settings: { ...c.settings, [field]: value } }));
  }

  async function saveSettings() {
    try {
      await api("/api/admin/settings", { method: "PUT", body: JSON.stringify(content.settings) });
      flash("Kaydedildi.");
    } catch (err) {
      setError(err.message);
    }
  }

  /* ---------- About values ---------- */

  function updateAboutValue(i, field, value) {
    setContent((c) => {
      const values = [...c.aboutValues];
      values[i] = { ...values[i], [field]: value };
      return { ...c, aboutValues: values };
    });
  }

  function addAboutValue() {
    setContent((c) => ({ ...c, aboutValues: [...c.aboutValues, { baslik: "", aciklama: "" }] }));
  }

  function removeAboutValue(i) {
    setContent((c) => ({ ...c, aboutValues: c.aboutValues.filter((_, idx) => idx !== i) }));
  }

  async function saveAboutValues() {
    try {
      await api("/api/admin/about-values", {
        method: "PUT",
        body: JSON.stringify({ values: content.aboutValues }),
      });
      flash("Kaydedildi.");
      fetchContent();
    } catch (err) {
      setError(err.message);
    }
  }

  /* ---------- Services ---------- */

  async function addService() {
    const title = window.prompt("Yeni hizmet başlığı:");
    if (!title?.trim()) return;
    const icon = window.prompt("Font Awesome ikon sınıfı (örn. fa fa-flask):", "fa fa-flask") || "";
    try {
      await api("/api/admin/services", { method: "POST", body: JSON.stringify({ title, icon }) });
      fetchContent();
    } catch (err) {
      setError(err.message);
    }
  }

  async function saveServiceMeta(service) {
    try {
      await api(`/api/admin/services/${service.id}`, {
        method: "PUT",
        body: JSON.stringify({ title: service.title, icon: service.icon }),
      });
      flash("Kaydedildi.");
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteService(id) {
    if (!window.confirm("Bu hizmet ve içindeki tüm alt başlıklar/resimler silinecek. Emin misiniz?")) return;
    try {
      await api(`/api/admin/services/${id}`, { method: "DELETE" });
      fetchContent();
    } catch (err) {
      setError(err.message);
    }
  }

  async function moveService(index, dir) {
    const services = [...content.services];
    const j = index + dir;
    if (j < 0 || j >= services.length) return;
    [services[index], services[j]] = [services[j], services[index]];
    setContent((c) => ({ ...c, services }));
    try {
      await api("/api/admin/services/reorder", {
        method: "PUT",
        body: JSON.stringify({ order: services.map((s) => s.id) }),
      });
    } catch (err) {
      setError(err.message);
    }
  }

  function updateServiceField(serviceId, field, value) {
    setContent((c) => ({
      ...c,
      services: c.services.map((s) => (s.id === serviceId ? { ...s, [field]: value } : s)),
    }));
  }

  /* ---------- Service items ---------- */

  async function addItem(serviceId) {
    const title = window.prompt("Yeni alt başlık:");
    if (!title?.trim()) return;
    try {
      await api(`/api/admin/services/${serviceId}/items`, {
        method: "POST",
        body: JSON.stringify({ title, description: "" }),
      });
      fetchContent();
    } catch (err) {
      setError(err.message);
    }
  }

  function updateItemField(serviceId, itemId, field, value) {
    setContent((c) => ({
      ...c,
      services: c.services.map((s) =>
        s.id !== serviceId
          ? s
          : { ...s, items: s.items.map((it) => (it.id === itemId ? { ...it, [field]: value } : it)) }
      ),
    }));
  }

  async function saveItem(item) {
    try {
      await api(`/api/admin/items/${item.id}`, {
        method: "PUT",
        body: JSON.stringify({ title: item.title, description: item.description }),
      });
      flash("Kaydedildi.");
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteItem(id) {
    if (!window.confirm("Bu alt başlık ve resimleri silinecek. Emin misiniz?")) return;
    try {
      await api(`/api/admin/items/${id}`, { method: "DELETE" });
      fetchContent();
    } catch (err) {
      setError(err.message);
    }
  }

  async function moveItem(service, index, dir) {
    const items = [...service.items];
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    [items[index], items[j]] = [items[j], items[index]];
    updateServiceField(service.id, "items", items);
    try {
      await api(`/api/admin/services/${service.id}/items/reorder`, {
        method: "PUT",
        body: JSON.stringify({ order: items.map((it) => it.id) }),
      });
    } catch (err) {
      setError(err.message);
    }
  }

  /* ---------- Images ---------- */

  async function uploadImage(itemId, file) {
    if (!file) return;
    const form = new FormData();
    form.append("image", file);
    try {
      await api(`/api/admin/items/${itemId}/images`, { method: "POST", body: form });
      fetchContent();
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteImage(imageId) {
    if (!window.confirm("Resim silinsin mi?")) return;
    try {
      await api(`/api/admin/images/${imageId}`, { method: "DELETE" });
      fetchContent();
    } catch (err) {
      setError(err.message);
    }
  }

  if (!authed) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 16 }}>
        <div style={{ width: "100%", maxWidth: 420, border: "1px solid #ddd", borderRadius: 16, padding: 16 }}>
          <h2 style={{ margin: 0 }}>Tomedyus Admin — İçerik</h2>
          <p style={{ opacity: 0.7, marginTop: 8 }}>Devam etmek için şifre gir.</p>

          <form onSubmit={login} style={{ marginTop: 12, display: "grid", gap: 10 }}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin şifresi"
              style={input}
            />
            <button disabled={loading || !password} style={btnPrimary}>
              {loading ? "Giriş yapılıyor..." : "Giriş"}
            </button>
            {error ? <div style={{ color: "crimson" }}>{error}</div> : null}
          </form>
        </div>
      </div>
    );
  }

  if (!content) {
    return <div style={{ padding: 16 }}>{loading ? "Yükleniyor…" : error || "Yükleniyor…"}</div>;
  }

  return (
    <div style={{ padding: 16, maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <h2 style={{ margin: 0 }}>İçerik Yönetimi</h2>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {savedMsg ? <span style={{ color: "#1e8e3e" }}>{savedMsg}</span> : null}
          <Link to="/tomedyus-admin/messages">Mesajlar →</Link>
        </div>
      </div>

      {error ? <div style={{ color: "crimson", marginTop: 10 }}>{error}</div> : null}

      {/* Admin şifresi */}
      <div style={card}>
        <h3 style={{ margin: 0 }}>Admin Şifresi</h3>
        <form onSubmit={changePassword}>
          <label style={label}>Mevcut şifre</label>
          <input
            type="password"
            style={input}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
          <label style={label}>Yeni şifre</label>
          <input
            type="password"
            style={input}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
          <label style={label}>Yeni şifre (tekrar)</label>
          <input
            type="password"
            style={input}
            value={newPassword2}
            onChange={(e) => setNewPassword2(e.target.value)}
            autoComplete="new-password"
          />
          <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
            <button type="submit" style={btnPrimary} disabled={!currentPassword || !newPassword}>
              Şifreyi değiştir
            </button>
            {pwMsg ? <span style={{ color: "#1e8e3e" }}>{pwMsg}</span> : null}
            {pwError ? <span style={{ color: "crimson" }}>{pwError}</span> : null}
          </div>
        </form>
      </div>

      {/* Header */}
      <div style={card}>
        <h3 style={{ margin: 0 }}>Header</h3>
        <label style={label}>Başlık</label>
        <input style={input} value={content.settings.headerTitle} onChange={(e) => updateSetting("headerTitle", e.target.value)} />
        <label style={label}>Alt başlık</label>
        <input style={input} value={content.settings.headerTitle2} onChange={(e) => updateSetting("headerTitle2", e.target.value)} />
        <label style={label}>Paragraf</label>
        <ReactQuill
          theme="snow"
          className="rq-compact"
          modules={quillModules}
          value={content.settings.headerParagraph}
          onChange={(v) => updateSetting("headerParagraph", v)}
        />
        <div style={{ marginTop: 12 }}>
          <button style={btnPrimary} onClick={saveSettings}>Kaydet</button>
        </div>
      </div>

      {/* About */}
      <div style={card}>
        <h3 style={{ margin: 0 }}>Hakkımızda</h3>
        <label style={label}>Paragraf</label>
        <ReactQuill
          theme="snow"
          className="rq-compact"
          modules={quillModules}
          value={content.settings.aboutParagraph}
          onChange={(v) => updateSetting("aboutParagraph", v)}
        />
        <label style={label}>Vizyon</label>
        <ReactQuill
          theme="snow"
          className="rq-compact"
          modules={quillModules}
          value={content.settings.aboutVizyon}
          onChange={(v) => updateSetting("aboutVizyon", v)}
        />
        <label style={label}>Misyon</label>
        <ReactQuill
          theme="snow"
          className="rq-compact"
          modules={quillModules}
          value={content.settings.aboutMisyon}
          onChange={(v) => updateSetting("aboutMisyon", v)}
        />
        <div style={{ marginTop: 12 }}>
          <button style={btnPrimary} onClick={saveSettings}>Kaydet</button>
        </div>
      </div>

      {/* Değerlerimiz */}
      <div style={card}>
        <h3 style={{ margin: 0 }}>Değerlerimiz</h3>
        {content.aboutValues.map((v, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "flex-start" }}>
            <input style={{ ...input, flex: "0 0 160px" }} placeholder="Başlık" value={v.baslik} onChange={(e) => updateAboutValue(i, "baslik", e.target.value)} />
            <input style={input} placeholder="Açıklama" value={v.aciklama} onChange={(e) => updateAboutValue(i, "aciklama", e.target.value)} />
            <button style={btnDanger} onClick={() => removeAboutValue(i)}>Sil</button>
          </div>
        ))}
        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
          <button style={btn} onClick={addAboutValue}>+ Değer ekle</button>
          <button style={btnPrimary} onClick={saveAboutValues}>Kaydet</button>
        </div>
      </div>

      {/* İletişim */}
      <div style={card}>
        <h3 style={{ margin: 0 }}>İletişim</h3>
        <label style={label}>Adres</label>
        <input style={input} value={content.settings.contactAddress} onChange={(e) => updateSetting("contactAddress", e.target.value)} />
        <label style={label}>Satış Telefon</label>
        <input style={input} value={content.settings.contactPhone} onChange={(e) => updateSetting("contactPhone", e.target.value)} />
        <label style={label}>Teknik Servis Telefon</label>
        <input style={input} value={content.settings.contactTechPhone} onChange={(e) => updateSetting("contactTechPhone", e.target.value)} />
        <label style={label}>Email</label>
        <input style={input} value={content.settings.contactEmail} onChange={(e) => updateSetting("contactEmail", e.target.value)} />
        <label style={label}>Facebook</label>
        <input style={input} value={content.settings.contactFacebook} onChange={(e) => updateSetting("contactFacebook", e.target.value)} />
        <label style={label}>Twitter</label>
        <input style={input} value={content.settings.contactTwitter} onChange={(e) => updateSetting("contactTwitter", e.target.value)} />
        <label style={label}>Youtube</label>
        <input style={input} value={content.settings.contactYoutube} onChange={(e) => updateSetting("contactYoutube", e.target.value)} />
        <div style={{ marginTop: 12 }}>
          <button style={btnPrimary} onClick={saveSettings}>Kaydet</button>
        </div>
      </div>

      {/* Hizmetler */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Hizmetler</h3>
          <button style={btn} onClick={addService}>+ Hizmet ekle</button>
        </div>

        {content.services.map((service, sIndex) => (
          <div key={service.id} style={{ ...card, background: "#fafafa" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                style={{ ...input, flex: "0 0 220px" }}
                value={service.icon}
                onChange={(e) => updateServiceField(service.id, "icon", e.target.value)}
                placeholder="fa fa-..."
              />
              <input
                style={input}
                value={service.title}
                onChange={(e) => updateServiceField(service.id, "title", e.target.value)}
              />
              <button style={btn} onClick={() => moveService(sIndex, -1)} disabled={sIndex === 0}>↑</button>
              <button style={btn} onClick={() => moveService(sIndex, 1)} disabled={sIndex === content.services.length - 1}>↓</button>
              <button style={btnPrimary} onClick={() => saveServiceMeta(service)}>Kaydet</button>
              <button style={btnDanger} onClick={() => deleteService(service.id)}>Sil</button>
            </div>

            <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: "3px solid #ddd" }}>
              {service.items.map((item, iIndex) => (
                <div key={item.id} style={{ marginTop: 14 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      style={input}
                      value={item.title}
                      onChange={(e) => updateItemField(service.id, item.id, "title", e.target.value)}
                    />
                    <button style={btn} onClick={() => moveItem(service, iIndex, -1)} disabled={iIndex === 0}>↑</button>
                    <button style={btn} onClick={() => moveItem(service, iIndex, 1)} disabled={iIndex === service.items.length - 1}>↓</button>
                    <button style={btnDanger} onClick={() => deleteItem(item.id)}>Sil</button>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <ReactQuill
                      theme="snow"
                      className="rq-compact rq-item"
                      modules={quillModules}
                      placeholder="Açıklama (opsiyonel — doldurulursa ana sayfada resimli/geniş kart olarak gösterilir)"
                      value={item.description || ""}
                      onChange={(v) => updateItemField(service.id, item.id, "description", v)}
                    />
                  </div>
                  <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button style={btn} onClick={() => saveItem(item)}>Kaydet</button>
                    <label style={{ ...btn, display: "inline-block" }}>
                      + Resim ekle
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => uploadImage(item.id, e.target.files?.[0])}
                      />
                    </label>
                    {item.images.map((img) => (
                      <div key={img.id} style={{ position: "relative" }}>
                        <img src={img.url} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8, border: "1px solid #ddd" }} />
                        <button
                          onClick={() => deleteImage(img.id)}
                          style={{
                            position: "absolute",
                            top: -6,
                            right: -6,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "none",
                            background: "crimson",
                            color: "#fff",
                            cursor: "pointer",
                            fontSize: 12,
                            lineHeight: "20px",
                            padding: 0,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 12 }}>
                <button style={btn} onClick={() => addItem(service.id)}>+ Alt başlık ekle</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
